import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Loader2 } from 'lucide-react';
import { AuthProvider } from './contexts/AuthContext';
import { MirandaProvider } from './contexts/MirandaContext';
import { DebugProvider } from './contexts/DebugContext';
import { MirandaFAB, MirandaSheet } from './components/miranda';
import ProtectedRoute from './components/ProtectedRoute';
import RequireRole from './components/RequireRole';
import DashboardLayout from './layouts/DashboardLayout';

// Eager loading - Pàgines crítiques (carregades immediatament)
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import ReservesPage from './pages/ReservesPage';
import ViatgersPage from './pages/ViatgersPage';
import FormulariPublicPage from './pages/FormulariPublicPage';
import CalendarV2Page from './pages/calendar-v2/CalendarV2Page';

// Lazy loading - Pàgines menys crítiques (code splitting)
const PersonsPage = lazy(() => import('./pages/PersonsPage'));
const FormsPublicsPage = lazy(() => import('./pages/FormsPublicsPage'));
const MossosPage = lazy(() => import('./pages/MossosPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const ManualPage = lazy(() => import('./pages/ManualPage'));

// Lazy loading - Pàgines /admin/* (code splitting agressiu)
const DataImporter = lazy(() => import('./components/DataImporter'));
const ConfigManager = lazy(() => import('./components/admin/ConfigManager'));
const TokenGeneratorPage = lazy(() =>
  import('./pages/admin/TokenGeneratorPage').then((m) => ({ default: m.TokenGeneratorPage })),
);
const InvoicesMonthPage = lazy(() => import('./pages/admin/billing/InvoicesMonthPage'));
const GuestPortalConfigPage = lazy(() =>
  import('./pages/admin/GuestPortalConfigPage').then((m) => ({ default: m.GuestPortalConfigPage })),
);
const ContractsPage = lazy(() => import('./pages/admin/ContractsPage'));
const SettingsPage = lazy(() => import('./pages/admin/SettingsPage'));
const LocksPage = lazy(() => import('./pages/admin/LocksPage'));
const PhysicalRoomsPage = lazy(() => import('./pages/admin/PhysicalRoomsPage'));
const AgentsPage = lazy(() => import('./pages/admin/AgentsPage'));
const NotificationsPage = lazy(() => import('./pages/admin/NotificationsPage'));
const ClimatePage = lazy(() => import('./pages/admin/ClimatePage'));
const EngineeringLogPage = lazy(() => import('./pages/admin/EngineeringLogPage'));
const WorkflowApprovalsPage = lazy(() => import('./pages/admin/WorkflowApprovalsPage'));
const InvoiceUploadPage = lazy(() => import('./pages/admin/InvoiceUploadPage'));
const WorkflowRunsPage = lazy(() => import('./pages/admin/WorkflowRunsPage'));
const CrawlersPage = lazy(() => import('./pages/admin/CrawlersPage'));
const CrawlerDetailPage = lazy(() => import('./pages/admin/CrawlerDetailPage'));
const PriceMonitoringPage = lazy(() => import('./pages/admin/PriceMonitoringPage'));
const BankMovementsPage = lazy(() => import('./pages/admin/BankMovementsPage'));
const PaymentChargesPage = lazy(() => import('./pages/admin/PaymentChargesPage'));
const AmazonImportPage = lazy(() => import('./pages/admin/AmazonImportPage'));
const MessagingTemplatesPage = lazy(() => import('./pages/admin/MessagingTemplatesPage'));
const ClientDetailPage = lazy(() => import('./pages/admin/ClientDetailPage'));
const CastinkPage = lazy(() => import('./pages/admin/CastinkPage'));
const WhatsappMetricsPage = lazy(() => import('./pages/admin/WhatsappMetricsPage'));
// Inbox de WhatsApp (`plans/whatsapp-inbox.md` §4): STAFF i ADMIN, la conversa oberta va a l'URL.
const WhatsappInboxPage = lazy(() => import('./pages/WhatsappInboxPage'));
// 🆕 Calendari històric del PMS antic (`plans/legacy-calendar-readonly.md`): només ADMIN,
// només lectura, i prou pesat (graella + date-picker) per anar en lazy.
const HistoricCalendarPage = lazy(() => import('./pages/calendar-historic/HistoricCalendarPage'));
const WorkflowsPage = lazy(() => import('./pages/WorkflowsPage'));
const PrecontactePage = lazy(() => import('./pages/PrecontactePage'));
// Telèfons de Booking (`plans/booking-phone-recovery.md`): germana de `/precontacte`, que és d'Expedia.
const PrecontacteBookingPage = lazy(() => import('./pages/PrecontacteBookingPage'));
const CasosPage = lazy(() => import('./pages/CasosPage'));
// Pàgina pública de pagament (`/pagament/:token`): l'hoste no ha de baixar-se el PMS sencer.
const PaymentPublicPage = lazy(() => import('./pages/PaymentPublicPage'));

// Lazy loading - Pàgines de test/demo
const ComponentsDemoPage = lazy(() => import('./pages/ComponentsDemoPage'));
const ComponentsTestPage = lazy(() => import('./pages/ComponentsTestPage'));
const TestBlockNotePage = lazy(() =>
  import('./pages/TestBlockNotePage').then((m) => ({ default: m.TestBlockNotePage })),
);
const PublicFormTestPage = lazy(() => import('./pages/PublicFormTestPage'));
const ReservationInfoTestPage = lazy(() => import('./pages/ReservationInfoTestPage'));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
  </div>
);

// Enllaços vells a `/admin/clients/:id` (abans de F5) → adreça canònica `/clients/:id`
const LegacyClientRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/clients/${id}`} replace />;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <DebugProvider>
          <MirandaProvider>
            <Toaster position="top-right" richColors />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/*
                Rutas públicas (sin autenticación).
                ⚠️ AUDIT DE SEGURETAT: aquestes rutes són PÚBLIQUES PER DISSENY.
                Llista canònica i rationale: `lib/constants/public-routes.ts`.
                `/formulari/:token` està protegit per token secret a backend
                (@Public decorator + FormsService.validateToken). NO afegir cap
                guard d'autenticació aquí — trencaria el flux de registre
                d'hostes (Mossos d'Esquadra).
              */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/formulari/:token" element={<FormulariPublicPage />} />
                {/* Pàgina de pagament de l'hoste (Stripe, F5): pública, protegida pel token de l'adreça
                    (`@Public` a `PaymentPageController`). Tampoc hi va cap guard. */}
                <Route path="/pagament/:token" element={<PaymentPublicPage />} />
                <Route path="/test/calendars" element={<PublicFormTestPage />} />
                <Route path="/test/reservation-info" element={<ReservationInfoTestPage />} />

                {/* Rutas protegidas (requieren autenticación) */}
                <Route element={<ProtectedRoute />}>
                  <Route element={<DashboardLayout />}>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    {/* E5 (runbook 5): el calendari v1 ja no existeix; es redirigeix per les adreces desades i la PWA */}
                    <Route path="/calendar" element={<Navigate to="/calendar-v2" replace />} />
                    <Route path="/clients" element={<ClientsPage />} />
                    {/* F5 de guest-data-quality — l'adreça d'un client; les rutes /admin/clients redirigeixen */}
                    <Route path="/clients/:id" element={<ClientDetailPage />} />
                    <Route path="/admin/clients" element={<Navigate to="/clients" replace />} />
                    <Route path="/admin/clients/:id" element={<LegacyClientRedirect />} />
                    <Route path="/reserves" element={<ReservesPage />} />
                    {/*
                      🆕 F1 — L'adreça d'una reserva: `/reserves/92725627` (o un grup amb `+`).
                      Renderitza el calendari v2 amb la fitxa oberta; la taula de `/reserves` es queda.
                    */}
                    <Route path="/reserves/:ids" element={<CalendarV2Page />} />
                    {/* 🆕 F3 — casos d'ús lligats a reserves */}
                    <Route path="/casos" element={<CasosPage />} />
                    <Route path="/viatgers" element={<ViatgersPage />} />
                    <Route path="/persons" element={<PersonsPage />} />
                    <Route path="/forms" element={<FormsPublicsPage />} />
                    <Route path="/config" element={<ConfigManager />} />
                    <Route path="/components-demo" element={<ComponentsDemoPage />} />
                    <Route path="/components-test" element={<ComponentsTestPage />} />
                    <Route path="/calendar-v2" element={<CalendarV2Page />} />
                    <Route path="/admin/invoices/upload" element={<InvoiceUploadPage />} />
                    <Route path="/admin/invoices/amazon-import" element={<AmazonImportPage />} />
                    {/*
                      B3 (runbook 4) i E5 (runbook 5): les rutes que el Sidebar només ensenya a ADMIN, incloses
                      /admin/tokens, /admin/billing i /calendari/historic. Les que STAFF veu al menú (upload i
                      Amazon de factures) queden fora; el backend fa la seva pròpia comprovació.
                    */}
                    <Route element={<RequireRole roles={['ADMIN']} />}>
                      <Route path="/calendari/historic" element={<HistoricCalendarPage />} />
                      {/* Runbook 18, F3: Mossos (TXT amb dades de tots els hostes) només per a ADMIN */}
                      <Route path="/mossos" element={<MossosPage />} />
                      <Route path="/admin/tokens" element={<TokenGeneratorPage />} />
                      {/* plans/facturacio-s1-lot-booking.md §7: factures del mes (lot de Booking) */}
                      <Route path="/admin/billing" element={<InvoicesMonthPage />} />
                      <Route path="/admin/guest-portal" element={<GuestPortalConfigPage />} />
                      <Route path="/admin/contracts" element={<ContractsPage />} />
                      <Route path="/admin/locks" element={<LocksPage />} />
                      <Route path="/admin/rooms" element={<PhysicalRoomsPage />} />
                      <Route path="/admin/climate" element={<ClimatePage />} />
                      <Route path="/admin/notifications" element={<NotificationsPage />} />
                      <Route path="/admin/engineering" element={<EngineeringLogPage />} />
                      <Route path="/admin/workflows" element={<WorkflowApprovalsPage />} />
                      <Route path="/admin/workflow-runs" element={<WorkflowRunsPage />} />
                      <Route path="/admin/agents" element={<AgentsPage />} />
                      <Route path="/admin/data-import" element={<DataImporter />} />
                      <Route path="/admin/crawlers" element={<CrawlersPage />} />
                      <Route path="/admin/crawlers/:id" element={<CrawlerDetailPage />} />
                      <Route path="/admin/prices" element={<PriceMonitoringPage />} />
                      <Route path="/admin/bank-movements" element={<BankMovementsPage />} />
                      {/* S3 de plans/stripe-payment-links.md: cobraments orfes, per revisar i avisos encallats */}
                      <Route path="/admin/cobraments" element={<PaymentChargesPage />} />
                      <Route path="/admin/messaging-templates" element={<MessagingTemplatesPage />} />
                      <Route path="/admin/whatsapp" element={<WhatsappMetricsPage />} />
                      <Route path="/admin/settings" element={<SettingsPage />} />
                    </Route>
                    <Route path="/workflows" element={<WorkflowsPage />} />
                    <Route path="/precontacte" element={<PrecontactePage />} />
                    <Route path="/precontacte-booking" element={<PrecontacteBookingPage />} />
                    <Route path="/test-blocknote" element={<TestBlockNotePage />} />
                    <Route path="/manual" element={<ManualPage />} />
                    <Route path="/chat" element={<ChatPage />} />
                    <Route path="/whatsapp/:conversationId?" element={<WhatsappInboxPage />} />
                    <Route path="/chat/:sessionId" element={<ChatPage />} />
                    <Route path="/tools/castink" element={<CastinkPage />} />
                  </Route>
                </Route>

                {/* Redirigir cualquier ruta no encontrada al dashboard */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Suspense>

            {/* Miranda AI Assistant - Global (persisteix entre navegacions) */}
            <MirandaFAB />
            <MirandaSheet />
          </MirandaProvider>
        </DebugProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
